import { useCallback, useMemo } from "react";
import { Box, Chip, CircularProgress, Tooltip } from "@mui/material";
import {
  BusinessOutlined,
  CancelOutlined,
  HomeWorkOutlined,
  InsertDriveFileOutlined,
  PersonOutlined,
  RadioButtonUncheckedOutlined,
  VerifiedOutlined,
  WarningAmberOutlined,
} from "@mui/icons-material";
import { amber, blue, green, grey, teal } from "@mui/material/colors";
import { AgGridReact } from "ag-grid-react";
import isNil from "lodash/isNil";

import AgGridTableWrapper from "../../../../components/agGrid/AgGridTableWrapper";
import {
  CARD_GRID_ACTIVE_ROW_STYLE,
  CARD_GRID_COL_DEF,
  CARD_GRID_SX,
  CARD_GRID_TWO_LINE_ROW_H,
  CARD_TABLE_BOX_SX,
} from "../../shared/cardGrid";
import Flx from "../../../../components/layout/Flx";
import Txt from "../../../../components/typography/Txt";
import { columnTypesDollar } from "../../../../_src_shared/utils/agGrid/columnTypes/columnTypesDollar";
import formatDollar from "../../../../_src_shared/utils/functions/formatDollar";
import { formatBorrowerObjectFullName } from "../../../../_src_shared/utils/functions/formatBorrowerObjectFullName";
import { formatAddressObjectFullAddress } from "../../../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import {
  LIQUIDITY_SOURCE_TYPES,
  getSourceMeta,
  sourceRequiresAssociations,
} from "./constants";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";

// Read borrowerIds / entityIds / loanDocumentIds / subjectPropertyIds from the
// new array shape, falling back to the legacy singular keys so unmigrated rows
// still render.
const getBorrowerIds = (s) =>
  Array.isArray(s?.borrowerIds)
    ? s.borrowerIds
    : s?.borrowerId
      ? [s.borrowerId]
      : [];
const getEntityIds = (s) =>
  Array.isArray(s?.entityIds) ? s.entityIds : s?.entityId ? [s.entityId] : [];
const getLoanDocumentIds = (s) =>
  Array.isArray(s?.loanDocumentIds)
    ? s.loanDocumentIds
    : s?.loanDocumentId
      ? [s.loanDocumentId]
      : [];
const getSubjectPropertyIds = (s) =>
  Array.isArray(s?.subjectPropertyIds)
    ? s.subjectPropertyIds
    : s?.subjectPropertyId
      ? [s.subjectPropertyId]
      : [];

const isCashOutSource = (s) =>
  s?.sourceType === LIQUIDITY_SOURCE_TYPES.CASH_OUT_PROCEEDS;

const TotalLabelCell = ({ count }) => (
  <Flx ac sx={{ height: "100%" }}>
    <Txt
      sx={{
        fontSize: 12,
        fontWeight: 700,
        //
        // color: "#ffffff",
        color: "#0f172a",
        // color: grey[700],
      }}
    >
      Total ({count} {count === 1 ? "source" : "sources"})
    </Txt>
  </Flx>
);

/* ───────────────── cell renderers ───────────────── */

const SourceCell = ({ sourceType, countedPct, notes }) => {
  const meta = getSourceMeta(sourceType);
  const Icon = meta.Icon;
  const showDiscountBadge = !isNil(countedPct) && countedPct !== 100;
  return (
    <Flx ac g={1} sx={{ height: "100%", minWidth: 0 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: 0.75,
          background: meta.iconBg,
          color: meta.color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          "& svg": { fontSize: 15 },
        }}
      >
        <Icon />
      </Box>
      <Flx column sx={{ minWidth: 0, lineHeight: 1.3 }}>
        <Flx ac g={0.75} sx={{ minWidth: 0 }}>
          <Txt
            component="div"
            sx={{ fontSize: 12, fontWeight: 600, color: grey[900] }}
          >
            {meta.label}
          </Txt>
          {showDiscountBadge && (
            <Tooltip
              title="Discounted from 100% toward validated liquidity"
              placement="top"
            >
              <Box
                component="span"
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: amber[900],
                  background: amber[50],
                  border: `1px solid ${amber[200]}`,
                  px: 0.625,
                  py: 0,
                  borderRadius: 0.75,
                  lineHeight: "16px",
                  whiteSpace: "nowrap",
                }}
              >
                {countedPct}% counted
              </Box>
            </Tooltip>
          )}
        </Flx>
        {notes && (
          <Tooltip title={notes} placement="top">
            <Box
              component="span"
              sx={{
                display: "block",
                fontSize: 10.5,
                color: grey[600],
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {notes}
            </Box>
          </Tooltip>
        )}
      </Flx>
    </Flx>
  );
};

const findEntityName = (loan, id) => {
  if (loan?.borrowerEntity?._id === id) return loan.borrowerEntity.entityName;
  const e = (loan?.entities || []).find((ee) => ee._id === id);
  return e?.entityName || null;
};

const findPropertyLabel = (loan, id) => {
  const p = (loan?.subjectProperties || []).find((pp) => pp._id === id);
  if (!p) return null;
  // Subject property address fields live nested on `p.address` — formatter
  // expects the address object itself, not the wrapping property record.
  const addr = p.address;
  const formatted = formatAddressObjectFullAddress(addr);
  return (
    (formatted && formatted !== "N/A" && formatted) ||
    addressLabel(addr) ||
    addr?.streetAddress ||
    "Property"
  );
};

const removalKey = (sourceId, type, id) => `${sourceId}:${type}:${id}`;

const AssociatedCell = ({
  source,
  loan,
  onRemoveAssociation,
  pendingRemovals,
}) => {
  const isPending = (type, id) =>
    !!pendingRemovals?.has?.(removalKey(source?._id, type, id));

  // Cash Out Proceeds: backend stores the subject properties producing the
  // proceeds. Show those instead of "Not required".
  if (isCashOutSource(source)) {
    const propertyIds = getSubjectPropertyIds(source);
    if (propertyIds.length === 0) {
      return <Txt sx={notRequiredSx}>No properties linked</Txt>;
    }
    return (
      <Flx ac g={0.5} sx={{ flexWrap: "wrap", minWidth: 0 }}>
        {propertyIds.map((id) => (
          <AssociationChip
            key={`p-${id}`}
            icon={<HomeWorkOutlined />}
            label={findPropertyLabel(loan, id) || "Property"}
            color={teal[800]}
            bg={teal[50]}
            loading={isPending("subjectPropertyIds", id)}
            onDelete={
              onRemoveAssociation
                ? () => onRemoveAssociation(source, "subjectPropertyIds", id)
                : undefined
            }
          />
        ))}
      </Flx>
    );
  }

  if (!sourceRequiresAssociations(source?.sourceType)) {
    return <Txt sx={notRequiredSx}>Not required</Txt>;
  }

  const borrowerIds = getBorrowerIds(source);
  const entityIds = getEntityIds(source);

  if (borrowerIds.length > 0 || entityIds.length > 0) {
    return (
      <Flx ac g={0.5} sx={{ flexWrap: "wrap", minWidth: 0 }}>
        {borrowerIds.map((id) => {
          const b = loan?.borrowers?.find((bb) => bb._id === id);
          return (
            <AssociationChip
              key={`b-${id}`}
              icon={<PersonOutlined />}
              label={formatBorrowerObjectFullName(b) || "Borrower"}
              loading={isPending("borrowerIds", id)}
              onDelete={
                onRemoveAssociation
                  ? () => onRemoveAssociation(source, "borrowerIds", id)
                  : undefined
              }
            />
          );
        })}
        {entityIds.map((id) => (
          <AssociationChip
            key={`e-${id}`}
            icon={<BusinessOutlined />}
            label={findEntityName(loan, id) || "Entity"}
            color={blue[700]}
            bg={blue[50]}
            loading={isPending("entityIds", id)}
            onDelete={
              onRemoveAssociation
                ? () => onRemoveAssociation(source, "entityIds", id)
                : undefined
            }
          />
        ))}
      </Flx>
    );
  }

  if (source?.unlinkedPartyName) {
    const isEntity = source.unlinkedPartyType === "entity";
    return (
      <Tooltip
        title="Unlinked party — add to loan or open a UW condition"
        placement="top"
      >
        <Flx ac g={0.5} sx={{ minWidth: 0 }}>
          <WarningAmberOutlined sx={{ fontSize: 14, color: amber[700] }} />
          <AssociationChip
            icon={isEntity ? <BusinessOutlined /> : <PersonOutlined />}
            label={source.unlinkedPartyName}
            color={amber[900]}
            bg={amber[50]}
          />
        </Flx>
      </Tooltip>
    );
  }
  if (source?.requiresFollowUp) {
    return <MissingChip label="Follow-up" />;
  }
  return <MissingChip label="Not set" />;
};

const DocumentCell = ({
  source,
  loan,
  onRemoveAssociation,
  pendingRemovals,
}) => {
  if (!sourceRequiresAssociations(source?.sourceType)) {
    return <Txt sx={notRequiredSx}>Not required</Txt>;
  }
  const docIds = getLoanDocumentIds(source);
  if (docIds.length === 0) {
    return <MissingChip label="Missing" />;
  }
  return (
    <Flx ac g={0.5} sx={{ flexWrap: "wrap", minWidth: 0 }}>
      {docIds.map((id) => {
        const doc = loan?.loanDocuments?.find((d) => d._id === id);
        const name =
          doc?.file_display_name || doc?.fileName || doc?.docType || "Document";
        const loading = !!pendingRemovals?.has?.(
          removalKey(source?._id, "loanDocumentIds", id),
        );
        return (
          <Tooltip key={id} title={name} placement="top">
            <Box
              component="span"
              sx={{ display: "inline-flex", maxWidth: 200 }}
            >
              <AssociationChip
                icon={<InsertDriveFileOutlined />}
                label={name}
                maxWidth={130}
                loading={loading}
                onDelete={
                  onRemoveAssociation
                    ? () => onRemoveAssociation(source, "loanDocumentIds", id)
                    : undefined
                }
              />
            </Box>
          </Tooltip>
        );
      })}
    </Flx>
  );
};

// Display-only — verified is toggled from the sidebar edit form, not the
// table. Showing a checkbox here would imply it's an inline edit affordance.
const VerifiedCell = ({ source }) => {
  const checked = !!source?.verified;
  return (
    <Tooltip
      title={
        checked
          ? "Verified — included in totalVerifiedLiquidity rollups"
          : "Not yet verified — excluded from verified liquidity rollups"
      }
      placement="top"
    >
      {/* Icon-only cell → centered on both axes (height:100% + ac/jc). */}
      <Flx ac jc sx={{ height: "100%" }}>
        {checked ? (
          <VerifiedOutlined sx={{ fontSize: 18, color: green[700] }} />
        ) : (
          <RadioButtonUncheckedOutlined
            sx={{ fontSize: 18, color: grey[400] }}
          />
        )}
      </Flx>
    </Tooltip>
  );
};

const AssociationChip = ({
  icon,
  label,
  color,
  bg,
  maxWidth = "100%",
  onDelete,
  loading,
}) => {
  // Custom deleteIcon — gives us control over onMouseDown so AG Grid's row
  // click handler (which fires off the click bubbling up to the cell DOM)
  // doesn't open the sidebar when the user hits ×.
  const stopAll = useCallback((e) => e.stopPropagation(), []);
  const deleteIconNode = onDelete ? (
    loading ? (
      <CircularProgress
        size={11}
        thickness={5}
        sx={{
          mr: "5px",
          ml: "1px",
          color: color || grey[500],
        }}
      />
    ) : (
      <CancelOutlined onMouseDown={stopAll} onClick={stopAll} />
    )
  ) : undefined;

  const handleDelete = useCallback(
    (e) => {
      e.stopPropagation();
      if (loading) return;
      onDelete?.(e);
    },
    [onDelete, loading],
  );

  return (
    <Chip
      size="small"
      icon={icon}
      label={label}
      onDelete={onDelete ? handleDelete : undefined}
      deleteIcon={deleteIconNode}
      sx={{
        maxWidth,
        background: bg || grey[100],
        color: color || grey[800],
        fontSize: "10px !important",
        height: 20,
        opacity: loading ? 0.7 : 1,
        "& .MuiChip-icon": {
          fontSize: "10px !important",
          color: `${color || grey[600]} !important`,
        },
        "& .MuiChip-label": {
          px: 0.75,
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        "& .MuiChip-deleteIcon": {
          fontSize: "14px !important",
          color: `${color || grey[500]} !important`,
          opacity: 0.6,
          "&:hover": { opacity: 1, color: `${color || grey[800]} !important` },
        },
      }}
    />
  );
};

const MissingChip = ({ label }) => (
  <Chip
    size="small"
    icon={<WarningAmberOutlined />}
    label={label}
    sx={{
      background: amber[50],
      color: amber[900],
      fontSize: "10px !important",
      height: 20,
      "& .MuiChip-icon": {
        fontSize: "13px !important",
        color: `${amber[700]} !important`,
      },
      "& .MuiChip-label": { px: 0.75 },
    }}
  />
);

const notRequiredSx = {
  fontSize: 11,
  color: grey[500],
  fontStyle: "italic",
};

const LiquiditySourcesTable = ({
  sources,
  loan,
  onRowClick,
  onRemoveAssociation,
  pendingRemovals,
  activeSourceId,
  quickFilter,
}) => {
  const rowData = useMemo(
    () =>
      (sources || []).map((s) => {
        const amount = Number(s.amount) || 0;
        const pct = isNil(s.countedPercentage)
          ? 100
          : Number(s.countedPercentage);
        // Prefer server-computed validatedAmount; fall back to local calc for
        // rows not yet persisted (optimistic adds, etc.).
        const validatedAmount = isNil(s.validatedAmount)
          ? amount * (pct / 100)
          : Number(s.validatedAmount);
        return { ...s, _validatedAmount: validatedAmount, _pct: pct };
      }),
    [sources],
  );

  const pinnedBottomRowData = useMemo(() => {
    const gross = rowData.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
    const validated = rowData.reduce(
      (sum, r) => sum + (Number(r._validatedAmount) || 0),
      0,
    );
    return [
      {
        _isTotal: true,
        _rowCount: rowData.length,
        amount: gross,
        _validatedAmount: validated,
      },
    ];
  }, [rowData]);

  const columnDefs = useMemo(
    () => [
      {
        // Counted % is now an inline badge in the source cell when < 100%,
        // not a dedicated column. A column of mostly "100%" cells with the
        // occasional discount is just chart junk — surface the exception
        // where it lives instead.
        headerName: "Source",
        field: "sourceType",
        minWidth: 220,
        flex: 1.5,
        // flex: 1.5,
        cellRenderer: (p) => {
          if (p?.data?._isTotal) {
            return <TotalLabelCell count={p.data._rowCount} />;
          }
          return (
            <SourceCell
              sourceType={p.value}
              countedPct={p?.data?._pct}
              notes={p?.data?.notes}
            />
          );
        },
      },
      {
        headerName: "Amount",
        field: "amount",
        type: "dollar",
        width: 130,
        flex: 0,
        // `num` = the admin grid's monospaced, right-aligned figures.
        cellClass: "num",
        headerClass: "num",
        valueFormatter: (p) => formatDollar(p.value, "—"),
      },
      {
        headerName: "Validated",
        field: "_validatedAmount",
        flex: 0,
        type: "dollar",
        width: 130,
        cellClass: "num",
        headerClass: "num",
        cellStyle: (p) => ({
          color: p?.data?.verified ? green[800] : "inherit",
          fontWeight: 600,
        }),
        valueFormatter: (p) => formatDollar(p.value, "—"),
      },
      {
        headerName: "Verified",
        field: "verified",
        flex: 0,
        width: 105,
        minWidth: 105,
        sortable: false,
        cellClass: "ctr",
        headerClass: "ctr",
        cellRenderer: (p) => {
          if (p?.data?._isTotal) return null;
          return <VerifiedCell source={p?.data} />;
        },
      },
      {
        headerName: "Associated",
        field: "borrowerIds",
        minWidth: 160,
        flex: 1,
        sortable: false,
        // Field is an array — disable auto cell-data-type inference so
        // AG Grid doesn't demand a valueFormatter for our cellRenderer.
        cellDataType: false,
        cellRenderer: (p) => {
          if (p?.data?._isTotal) return null;
          return (
            <AssociatedCell
              source={p?.data}
              loan={loan}
              onRemoveAssociation={onRemoveAssociation}
              pendingRemovals={pendingRemovals}
            />
          );
        },
      },
      {
        headerName: "Documents",
        field: "loanDocumentIds",
        minWidth: 180,
        flex: 1,
        sortable: false,
        cellDataType: false,
        cellRenderer: (p) => {
          if (p?.data?._isTotal) return null;
          return (
            <DocumentCell
              source={p?.data}
              loan={loan}
              onRemoveAssociation={onRemoveAssociation}
              pendingRemovals={pendingRemovals}
            />
          );
        },
      },
    ],
    [loan, onRemoveAssociation, pendingRemovals],
  );

  const defaultColDef = useMemo(
    () => ({
      ...CARD_GRID_COL_DEF,
      filter: true,
      flex: 1,
      minWidth: 100,
      cellStyle: {
        display: "flex",
        alignItems: "center",
        lineHeight: "18px",
      },
    }),
    [],
  );

  const columnTypes = useMemo(() => ({ ...columnTypesDollar() }), []);

  const handleRowClicked = useCallback(
    (params) => {
      if (typeof onRowClick === "function") onRowClick(params?.data);
    },
    [onRowClick],
  );

  const onFirstDataRendered = useCallback((params) => {
    // params?.api?.autoSizeAllColumns();
  }, []);

  // Highlight whichever row matches the source currently open in the sidebar
  // edit pane. Inset left rule + soft tint so the user can always see which
  // row their changes apply to.
  const getRowStyle = useCallback(
    (params) => {
      if (params?.node?.rowPinned) return undefined;
      if (
        activeSourceId &&
        params?.data?._id &&
        params.data._id === activeSourceId
      ) {
        return CARD_GRID_ACTIVE_ROW_STYLE;
      }
      return undefined;
    },
    [activeSourceId],
  );

  return (
    // The Admin → DSCR Pricing table look: a bordered block around a grid that
    // draws `noBorder` itself, with `CARD_GRID_SX` supplying the header / row
    // rules / tabular figures. Only the row-affordance bits are local.
    <Box sx={CARD_TABLE_BOX_SX}>
      <AgGridTableWrapper
        compactTable
        noBorder
        suppressMinHeightWhenPopulated={rowData}
        sx={{
          ...CARD_GRID_SX,
          // Hover state on data rows replaces the "Click any line item to
          // edit" instructional text. Affordance, not a label.
          ".ag-row:not(.ag-row-pinned)": { cursor: "pointer" },
          // The totals row: heavier figures over a rule that separates it from
          // the sources above.
          ".ag-row-pinned": {
            fontWeight: 600,
            borderTop: `1px solid ${grey[200]}`,
          },
        }}
      >
        <AgGridReact
          rowData={rowData}
          pinnedBottomRowData={pinnedBottomRowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          columnTypes={columnTypes}
          quickFilterText={quickFilter}
          domLayout="autoHeight"
          onRowClicked={handleRowClicked}
          onFirstDataRendered={onFirstDataRendered}
          getRowStyle={getRowStyle}
          rowHeight={CARD_GRID_TWO_LINE_ROW_H}
        />
      </AgGridTableWrapper>
    </Box>
  );
};

export default LiquiditySourcesTable;
